/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { useLocalization } from '@/data/Localization';
import SearchIcon from '@mui/icons-material/Search';
import { Box, CircularProgress, IconButton, InputAdornment, TextField } from '@mui/material';
import { FC, useCallback, useEffect, useRef } from 'react';
import { ContentProvider } from '@/data/context/content';
import { ProductOverlay } from './ProductOverlay';
import { HelloRetailHeaderSearchSX } from '../../styles/search';
import { useHelloRetail } from '@/data/Content/_HelloRetailTasks';
import { useHelloRetailSearch } from '@/data/Content/HelloRetailSearch';
import { useSearchNavigation } from '@/data/Content/SearchNavigation';
import { useDeviceType } from '@/data/deviceDetection';
import { useNextRouter } from '@/data/Content/_NextRouter';

export const HeaderSearch: FC<{ mobile?: boolean }> = ({ mobile }) => {

	const SearchNLS = useLocalization('SearchBar');
	const uniqueId = `type-ahead-site-search-${mobile ? 'mobile' : 'desktop'}`;
	const router = useNextRouter();

	const { isMobile } = useDeviceType();

	const helloRetailSearchValues = useHelloRetailSearch();

	const { routeToPdp } = useHelloRetail();

	const { onSubmit: sumbitSearch } = useSearchNavigation();

	const {
		searchValue,
		setSearchValue,
		loading,
		products,
		showOverLay,
		setShowOverLay,
		redirect
	} = helloRetailSearchValues;

	const onSubmit = useCallback(async ({ label, href }: {label: string;href?: string;}) => {
		if(redirect?.at(0)?.originalUrl){
			router.push(redirect[0].originalUrl);
		}else{
			sumbitSearch({ label, href })
		}
	},[redirect, router]);

	useEffect(()=>{
		if(products?.length  && products?.length == 1){ 
			const productData = products?.at(0);
			if(productData && (productData.ean === searchValue || productData?.partNumber === searchValue) ){
				routeToPdp(products?.at(0)?.seo.href as string);
			}			
		}		
	},[products?.length  && products?.length == 1]);

	const enterPressed = useRef(false);

	useEffect(() => {
		if (typeof window !== "undefined" && isMobile && showOverLay) {
			const el = document.querySelector<HTMLInputElement>("#header_search_input");
			if (el) {
				const rect = el.getBoundingClientRect();
				const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
				setTimeout(()=>window.scrollTo({
					top: rect.top + scrollTop - 15, // offset
					behavior: "smooth",
				}),500);
			}
		}
	}, [showOverLay, isMobile]);


	return (
		<Box tabIndex={-1}>
			<TextField
				required
				inputProps={{ 'data-testid': uniqueId, 'aria-label': SearchNLS.SearchField.t() }}
				variant='outlined'
				fullWidth
				sx={HelloRetailHeaderSearchSX({ isMobile: mobile, showOverLay })}
				value={searchValue}
				onChange={(event) => {
					setSearchValue(event.target.value);
					setShowOverLay(true)
				}}
				autoComplete="off"
				onFocus={()=>{
					setShowOverLay(true)
				}}
				id="header_search_input"
				InputProps={{
					placeholder: SearchNLS.SearchField.t(),
					slotProps: {
						input: { role: undefined, 'aria-expanded': undefined },
					},
					size: 'small',
					type: 'input',
					onKeyDown: (e) => {
						if (e.code === 'Enter') {
							e.preventDefault();
							enterPressed.current = true; 
							if (e.currentTarget.value) {
								onSubmit({ label: e.currentTarget.value});
							}
						}
					},
					endAdornment: (
						<InputAdornment position="end">
							<IconButton
								aria-label={SearchNLS.SearchIconLabel.t()}
								edge="end"
								size="medium"
								sx={{p:1}}
								onClick={()=>onSubmit({label:searchValue})}
							>
								{loading ? 
									<CircularProgress size={20} /> 
									: <SearchIcon tabIndex={-1} sx={{color:'black'}}/>//To ignore this button from keyboard navigation
								}
							</IconButton>
						</InputAdornment>
					),
				}}
			/>
			{showOverLay ?
				<ContentProvider value={{...helloRetailSearchValues}} >
					<ProductOverlay />
				</ContentProvider>
			: null}
		</Box>
	);
};
